import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';

// Use environment variables (recommended) or provide values directly for development
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase configuration missing!');
  console.log('URL configured:', !!supabaseUrl);
  console.log('Key configured:', !!supabaseAnonKey);
}

// Create a typed Supabase client for better code assistance
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

// Customer type definition
export type Customer = {
  id?: string;
  name: string;
  email: string;
  plan: string;
  status: string;
  mrr: number;
  join_date?: string;
  health_score: number;
  user_id?: string;
}

// Subscription Plan type definition
export type SubscriptionPlan = {
  id?: string;
  name: string;
  description?: string;
  price: number;
  billing_period?: 'monthly' | 'annual';
  features?: Record<string, any>; // JSON object with features
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Subscription type definition
export type Subscription = {
  id?: string;
  customer_id: string;
  plan_id: string;
  status: string; // active, trialing, canceled, etc.
  start_date: string;
  end_date?: string;
  trial_end_date?: string;
  cancel_at_period_end?: boolean;
  created_at?: string;
  updated_at?: string;
}

// Revenue data type
export type RevenueData = {
  id?: string;
  date: string; // YYYY-MM-DD format
  new_mrr: number;
  churned_mrr: number;
  expansion_mrr: number;
  contraction_mrr: number;
  net_new_mrr: number;
  total_mrr: number;
  user_id?: string;
}

// Invoice type definition
export type Invoice = {
  id?: string;
  subscription_id: string;
  amount: number;
  status: 'Unpaid' | 'Paid' | 'Partially Paid' | 'Overdue' | 'Cancelled';
  due_date: string;
  paid_at?: string;
  total_paid?: number;
  created_at?: string;
  updated_at?: string;
  invoice_number?: number;
}

// Invoice statuses for form selections
export const INVOICE_STATUSES = ['Unpaid', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled'] as const;

// Payment type definition
export type Payment = {
  id?: string;
  invoice_id: string;
  payment_method: string; // card, bank, etc.
  amount: number;
  status: string; // success, failed, pending
  transaction_id?: string;
  paid_at: string;
  created_at?: string;
  updated_at?: string;
}

// Tenant type definition
export type Tenant = {
  id?: string;
  company_id: string;
  name: string;
  status: string;
  health_score: number;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
}

// User type definition
export type User = {
  id?: string;
  name: string;
  username: string;
  email: string;
  phonenumber: string;
  created_at?: string;
  updated_at?: string;
}

// Mock data for fallback when Supabase tables aren't set up
const mockCustomers: Customer[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    email: 'sarah@example.com',
    plan: 'Enterprise',
    status: 'Active',
    mrr: 199,
    join_date: '2023-09-15',
    health_score: 98,
    user_id: '00000000-0000-0000-0000-000000000000'
  },
  {
    id: '2',
    name: 'Michael Chen',
    email: 'michael@example.com',
    plan: 'Pro',
    status: 'Active',
    mrr: 49,
    join_date: '2023-08-22',
    health_score: 85,
    user_id: '00000000-0000-0000-0000-000000000000'
  },
  {
    id: '3',
    name: 'Emma Wilson',
    email: 'emma@example.com',
    plan: 'Basic',
    status: 'Trial',
    mrr: 0,
    join_date: '2023-09-25',
    health_score: 75,
    user_id: '00000000-0000-0000-0000-000000000000'
  },
  {
    id: '4',
    name: 'David Rodriguez',
    email: 'david@example.com',
    plan: 'Pro',
    status: 'Active',
    mrr: 49,
    join_date: '2023-07-05',
    health_score: 92,
    user_id: '00000000-0000-0000-0000-000000000000'
  }
];

// Mock subscription plan data
const mockSubscriptionPlans: SubscriptionPlan[] = [
  {
    id: '11111111-1111-1111-1111-111111111111', 
    name: 'Basic',
    description: 'For individuals and small projects',
    price: 9.99,
    billing_period: 'monthly',
    features: { 
      storage: '5GB', 
      users: 1, 
      support: 'Email'
    },
    active: true
  },
  {
    id: '22222222-2222-2222-2222-222222222222', 
    name: 'Pro',
    description: 'For professionals and growing teams',
    price: 49.99,
    billing_period: 'monthly',
    features: { 
      storage: '50GB', 
      users: 10, 
      support: 'Priority Email + Chat'
    },
    active: true
  },
  {
    id: '33333333-3333-3333-3333-333333333333', 
    name: 'Enterprise',
    description: 'For large organizations and complex needs',
    price: 199.99,
    billing_period: 'monthly',
    features: { 
      storage: 'Unlimited', 
      users: 'Unlimited', 
      support: '24/7 Phone + Priority Support' 
    },
    active: true
  }
];

// Mock revenue data
const mockRevenueData: RevenueData[] = [
  {
    id: '1',
    date: '2023-01-01',
    new_mrr: 500,
    churned_mrr: 50,
    expansion_mrr: 100,
    contraction_mrr: 25,
    net_new_mrr: 525,
    total_mrr: 1500
  },
  {
    id: '2',
    date: '2023-02-01',
    new_mrr: 750,
    churned_mrr: 100,
    expansion_mrr: 200,
    contraction_mrr: 50,
    net_new_mrr: 800,
    total_mrr: 2300
  },
  {
    id: '3',
    date: '2023-03-01',
    new_mrr: 1000,
    churned_mrr: 150,
    expansion_mrr: 300,
    contraction_mrr: 100,
    net_new_mrr: 1050,
    total_mrr: 3350
  },
  {
    id: '4',
    date: '2023-04-01',
    new_mrr: 1200,
    churned_mrr: 200,
    expansion_mrr: 400,
    contraction_mrr: 150,
    net_new_mrr: 1250,
    total_mrr: 4600
  },
  {
    id: '5',
    date: '2023-05-01',
    new_mrr: 1500,
    churned_mrr: 300,
    expansion_mrr: 500,
    contraction_mrr: 200,
    net_new_mrr: 1500,
    total_mrr: 6100
  }
];

// Mock subscription data
const mockSubscriptions: Subscription[] = [
  {
    id: '1',
    customer_id: '1', // Sarah Johnson
    plan_id: '33333333-3333-3333-3333-333333333333', // Enterprise
    status: 'active',
    start_date: '2023-09-15',
    end_date: '2024-09-15',
    created_at: '2023-09-15T10:00:00Z',
    updated_at: '2023-09-15T10:00:00Z'
  },
  {
    id: '2',
    customer_id: '2', // Michael Chen
    plan_id: '22222222-2222-2222-2222-222222222222', // Pro
    status: 'active',
    start_date: '2023-08-22',
    end_date: '2024-08-22',
    created_at: '2023-08-22T14:30:00Z',
    updated_at: '2023-08-22T14:30:00Z'
  },
  {
    id: '3',
    customer_id: '3', // Emma Wilson
    plan_id: '11111111-1111-1111-1111-111111111111', // Basic
    status: 'trialing',
    start_date: '2023-09-25',
    trial_end_date: '2023-10-09',
    created_at: '2023-09-25T09:15:00Z',
    updated_at: '2023-09-25T09:15:00Z'
  },
  {
    id: '4',
    customer_id: '4', // David Rodriguez
    plan_id: '22222222-2222-2222-2222-222222222222', // Pro
    status: 'canceled',
    start_date: '2023-07-05',
    end_date: '2023-08-05',
    cancel_at_period_end: true,
    created_at: '2023-07-05T16:45:00Z',
    updated_at: '2023-07-25T11:20:00Z'
  }
];

// Mock tenant data
const mockTenants: Tenant[] = [
  {
    id: '1',
    company_id: '1', // Sarah Johnson's company
    name: 'Engineering Department',
    status: 'Active',
    health_score: 92,
    user_id: '00000000-0000-0000-0000-000000000000'
  },
  {
    id: '2',
    company_id: '1', // Sarah Johnson's company
    name: 'Marketing Team',
    status: 'Active',
    health_score: 85,
    user_id: '00000000-0000-0000-0000-000000000000'
  },
  {
    id: '3',
    company_id: '2', // Michael Chen's company
    name: 'Sales Division',
    status: 'At Risk',
    health_score: 65,
    user_id: '00000000-0000-0000-0000-000000000000'
  },
  {
    id: '4',
    company_id: '4', // David Rodriguez's company
    name: 'Product Development',
    status: 'Active',
    health_score: 88,
    user_id: '00000000-0000-0000-0000-000000000000'
  }
];

// Mock invoices data
const mockInvoices: Invoice[] = [
  {
    id: '1',
    subscription_id: '1', // Sarah Johnson's subscription
    amount: 199,
    status: 'paid',
    due_date: '2023-10-15T00:00:00Z',
    paid_at: '2023-10-13T15:30:00Z',
    created_at: '2023-10-01T10:00:00Z',
    updated_at: '2023-10-13T15:30:00Z'
  },
  {
    id: '2',
    subscription_id: '2', // Michael Chen's subscription
    amount: 49,
    status: 'paid',
    due_date: '2023-10-22T00:00:00Z',
    paid_at: '2023-10-20T12:30:00Z',
    created_at: '2023-10-07T14:30:00Z',
    updated_at: '2023-10-20T12:30:00Z'
  },
  {
    id: '3',
    subscription_id: '1', // Sarah Johnson's subscription
    amount: 199,
    status: 'pending',
    due_date: '2023-11-15T00:00:00Z',
    created_at: '2023-11-01T10:00:00Z',
    updated_at: '2023-11-01T10:00:00Z'
  },
  {
    id: '4',
    subscription_id: '4', // David Rodriguez's subscription
    amount: 49,
    status: 'overdue',
    due_date: '2023-09-05T00:00:00Z',
    created_at: '2023-08-21T16:45:00Z',
    updated_at: '2023-09-06T09:00:00Z'
  }
];

// Mock payments data
const mockPayments: Payment[] = [
  {
    id: '1',
    invoice_id: '1', // Sarah Johnson's invoice
    payment_method: 'card',
    amount: 199,
    status: 'success',
    transaction_id: 'txn_1234567890',
    paid_at: '2023-10-13T15:30:00Z',
    created_at: '2023-10-13T15:30:00Z',
    updated_at: '2023-10-13T15:30:00Z'
  },
  {
    id: '2',
    invoice_id: '2', // Michael Chen's invoice
    payment_method: 'bank_transfer',
    amount: 49,
    status: 'success',
    transaction_id: 'txn_0987654321',
    paid_at: '2023-10-20T12:30:00Z',
    created_at: '2023-10-20T12:30:00Z',
    updated_at: '2023-10-20T12:30:00Z'
  },
  {
    id: '3',
    invoice_id: '4', // David Rodriguez's invoice
    payment_method: 'card',
    amount: 49,
    status: 'failed',
    transaction_id: 'txn_5678901234',
    paid_at: '2023-09-05T14:20:00Z',
    created_at: '2023-09-05T14:20:00Z',
    updated_at: '2023-09-05T14:20:00Z'
  }
];

// Mock users data
const mockUsers: User[] = [
  {
    id: '1',
    name: 'John Smith',
    username: 'johnsmith',
    email: 'john.smith@example.com',
    phonenumber: '+1-555-123-4567',
    created_at: '2023-05-15T10:00:00Z',
    updated_at: '2023-05-15T10:00:00Z'
  },
  {
    id: '2',
    name: 'Jane Doe',
    username: 'janedoe',
    email: 'jane.doe@example.com',
    phonenumber: '+1-555-987-6543',
    created_at: '2023-06-20T14:30:00Z',
    updated_at: '2023-06-20T14:30:00Z'
  },
  {
    id: '3',
    name: 'Robert Johnson',
    username: 'rjohnson',
    email: 'robert.johnson@example.com',
    phonenumber: '+1-555-555-5555',
    created_at: '2023-07-10T09:15:00Z',
    updated_at: '2023-07-10T09:15:00Z'
  },
  {
    id: '4',
    name: 'Emily Wilson',
    username: 'emilyw',
    email: 'emily.wilson@example.com',
    phonenumber: '+1-555-444-3333',
    created_at: '2023-08-05T16:45:00Z',
    updated_at: '2023-08-05T16:45:00Z'
  }
];

// Customer services
export const customerServices = {
  // Get all customers
  async getAll() {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      return { data: mockCustomers, error: null };
    }
    
    return { data, error };
  },

  // Get customer by id
  async getById(id: string) {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const mockCustomer = mockCustomers.find(c => c.id === id);
      return { data: mockCustomer || null, error: mockCustomer ? null : new Error('Customer not found') };
    }
    
    return { data, error };
  },

  // Create a new customer
  async create(customer: Customer) {
    const { data, error } = await supabase
      .from('customers')
      .insert([customer])
      .select();
      
    if (error) {
      console.warn('Error creating in Supabase, using mock data:', error);
      const newCustomer = { ...customer, id: crypto.randomUUID() };
      mockCustomers.push(newCustomer);
      return { data: [newCustomer], error: null };
    }
    
    return { data, error };
  },

  // Update customer
  async update(id: string, updates: Partial<Customer>) {
    const { data, error } = await supabase
      .from('customers')
      .update(updates)
      .eq('id', id)
      .select();
      
    if (error) {
      console.warn('Error updating in Supabase, using mock data:', error);
      const customerIndex = mockCustomers.findIndex(c => c.id === id);
      if (customerIndex !== -1) {
        mockCustomers[customerIndex] = { ...mockCustomers[customerIndex], ...updates };
        return { data: [mockCustomers[customerIndex]], error: null };
      }
      return { data: null, error: new Error('Customer not found') };
    }
    
    return { data, error };
  },

  // Delete customer
  async delete(id: string) {
    const { data, error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.warn('Error deleting from Supabase, using mock data:', error);
      const customerIndex = mockCustomers.findIndex(c => c.id === id);
      if (customerIndex !== -1) {
        mockCustomers.splice(customerIndex, 1);
        return { data: null, error: null };
      }
      return { data: null, error: new Error('Customer not found') };
    }
    
    return { data, error };
  }
};

// Subscription plan services
export const subscriptionPlanServices = {
  // Get all subscription plans
  async getAll() {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('active', true)
        .order('price', { ascending: true });
      
      if (error) {
        console.error('Error fetching subscription plans from database:', error);
        throw error;
      }
      
      // If we got no data but no error, it might be the first run, so insert the default plans
      if (!data || data.length === 0) {
        console.log('No subscription plans found in database, creating default plans');
        await this.createDefaultPlans();
        
        // Retry the fetch
        const retryResult = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('active', true)
          .order('price', { ascending: true });
          
        return retryResult;
      }
      
      return { data, error };
    } catch (err) {
      console.error('Failed to get subscription plans:', err);
      // Only use mock data as an absolute last resort in development
      if (import.meta.env.DEV) {
        console.warn('Falling back to mock data in development environment only');
        return { data: mockSubscriptionPlans, error: null };
      }
      return { data: null, error: err };
    }
  },

  // Get subscription plan by id
  async getById(id: string) {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) {
        console.error('Error fetching subscription plan by ID:', error);
        throw error;
      }
      
      return { data, error };
    } catch (err) {
      console.error(`Failed to get subscription plan with id ${id}:`, err);
      // Only use mock data as an absolute last resort in development
      if (import.meta.env.DEV) {
        console.warn('Falling back to mock data in development environment only');
        const mockPlan = mockSubscriptionPlans.find(p => p.id === id);
        return { data: mockPlan || null, error: mockPlan ? null : new Error('Plan not found') };
      }
      return { data: null, error: err };
    }
  },

  // Create a new subscription plan
  async create(plan: SubscriptionPlan) {
    try {
      // Ensure required fields are present
      if (!plan.name || !plan.price) {
        throw new Error('Missing required subscription plan fields (name, price)');
      }
      
      // Format the data correctly for Supabase
      const formattedPlan = {
        ...plan,
        // Generate UUID if not provided
        id: plan.id || crypto.randomUUID(),
        // Ensure active state
        active: plan.active === undefined ? true : plan.active,
        // Set created_at and updated_at
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('subscription_plans')
        .insert([formattedPlan])
        .select();
        
      if (error) {
        console.error('Error creating subscription plan in Supabase:', error);
        throw error;
      }
      
      return { data, error };
    } catch (err) {
      console.error('Error in subscription plan creation:', err);
      return { data: null, error: err };
    }
  },

  // Update subscription plan
  async update(id: string, updates: Partial<SubscriptionPlan>) {
    try {
      if (!id) {
        throw new Error('Subscription Plan ID is required for updates');
      }

      // Format the data correctly for Supabase
      const formattedUpdates = {
        ...updates,
        // Ensure updated_at is current
        updated_at: new Date().toISOString()
      };
      const { data, error } = await supabase
        .from('subscription_plans')
        .update(formattedUpdates)
        .eq('id', id)
        .select();
        
      if (error) {
        console.error('Error updating subscription plan in Supabase:', error);
        throw error;
      }
      
      return { data, error };
    } catch (err) {
      console.error('Error in subscription plan update:', err);
      return { data: null, error: err };
    }
  },

  // Delete subscription plan (or deactivate)
  async delete(id: string) {
    // Instead of actually deleting, we typically just deactivate plans
    const { data, error } = await supabase
      .from('subscription_plans')
      .update({ active: false })
      .eq('id', id)
      .select();
      
    if (error) {
      console.warn('Error deactivating plan in Supabase, using mock data:', error);
      const planIndex = mockSubscriptionPlans.findIndex(p => p.id === id);
      if (planIndex !== -1) {
        mockSubscriptionPlans[planIndex].active = false;
        return { data: [mockSubscriptionPlans[planIndex]], error: null };
      }
      return { data: null, error: new Error('Plan not found') };
    }
    
    return { data, error };
  },
  
  // Create default subscription plans in the database
  async createDefaultPlans() {
    try {
      console.log('Creating default subscription plans in the database...');
      
      // Define default plans with proper UUIDs
      const defaultPlans = [
        {
          id: '11111111-1111-1111-1111-111111111111', 
          name: 'Basic',
          description: 'For individuals and small projects',
          price: 9.99,
          billing_period: 'monthly',
          features: { 
            storage: '5GB', 
            users: 1, 
            support: 'Email'
          },
          active: true
        },
        {
          id: '22222222-2222-2222-2222-222222222222', 
          name: 'Pro',
          description: 'For professionals and growing teams',
          price: 49.99,
          billing_period: 'monthly',
          features: { 
            storage: '50GB', 
            users: 10, 
            support: 'Priority Email + Chat'
          },
          active: true
        },
        {
          id: '33333333-3333-3333-3333-333333333333', 
          name: 'Enterprise',
          description: 'For large organizations and complex needs',
          price: 199.99,
          billing_period: 'monthly',
          features: { 
            storage: 'Unlimited', 
            users: 'Unlimited', 
            support: '24/7 Phone + Priority Support' 
          },
          active: true
        }
      ];
      
      // Insert all default plans with timestamps
      const plansWithTimestamps = defaultPlans.map(plan => ({
        ...plan,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('subscription_plans')
        .insert(plansWithTimestamps)
        .select();
      
      if (error) {
        console.error('Error creating default subscription plans:', error);
        throw error;
      }
      
      console.log('Successfully created default subscription plans:', data);
      return { data, error };
    } catch (err) {
      console.error('Failed to create default subscription plans:', err);
      return { data: null, error: err };
    }
  }
};

// Subscription services
export const subscriptionServices = {
  // Get all subscriptions
  async getAll() {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      return { data: mockSubscriptions, error: null };
    }
    
    return { data, error };
  },

  // Get subscription by id
  async getById(id: string) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const mockSubscription = mockSubscriptions.find(s => s.id === id);
      return { data: mockSubscription || null, error: mockSubscription ? null : new Error('Subscription not found') };
    }
    
    return { data, error };
  },

  // Get subscriptions by customer_id
  async getByCustomer(customerId: string) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const customerSubscriptions = mockSubscriptions.filter(s => s.customer_id === customerId);
      return { data: customerSubscriptions, error: null };
    }
    
    return { data, error };
  },

  // Create a new subscription
  async create(subscription: Subscription) {
    try {
      // Ensure required fields are present
      if (!subscription.customer_id || !subscription.plan_id || !subscription.status || !subscription.start_date) {
        throw new Error('Missing required subscription fields');
      }

      // Format the data correctly for Supabase
      const formattedSubscription = {
        ...subscription,
        // Format dates if necessary
        start_date: subscription.start_date,
        end_date: subscription.end_date || null,
        trial_end_date: subscription.trial_end_date || null,
        // Make sure cancel_at_period_end is boolean
        cancel_at_period_end: subscription.cancel_at_period_end || false,
      };
      
      const { data, error } = await supabase
        .from('subscriptions')
        .insert([formattedSubscription])
        .select();
      
      // If we get a database error
      if (error) {
        console.error('Error creating subscription in Supabase:', error);
        
        // Only use mock data in development
        if (import.meta.env.DEV) {
          console.warn('Using mock data as fallback in development');
          const newSubscription = { ...subscription, id: crypto.randomUUID() };
          mockSubscriptions.push(newSubscription);
          return { data: [newSubscription], error: null };
        }
        
        throw error;
      }
      
      return { data, error };
    } catch (err: any) {
      console.error('Error in subscription creation:', err);
      return { data: null, error: err };
    }
  },

  // Update subscription
  async update(id: string, updates: Partial<Subscription>) {
    try {
      if (!id) {
        throw new Error('Subscription ID is required for updates');
      }

      // Format the data correctly for Supabase
      const formattedUpdates = {
        ...updates,
        // Format dates if present
        start_date: updates.start_date,
        end_date: updates.end_date,
        trial_end_date: updates.trial_end_date,
        // Ensure updated_at is current
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('subscriptions')
        .update(formattedUpdates)
        .eq('id', id)
        .select();
      
      if (error) {
        console.error('Error updating subscription in Supabase:', error);
        
        // Only use mock data in development
        if (import.meta.env.DEV) {
          console.warn('Using mock data as fallback in development');
          const subscriptionIndex = mockSubscriptions.findIndex(s => s.id === id);
          if (subscriptionIndex !== -1) {
            mockSubscriptions[subscriptionIndex] = { ...mockSubscriptions[subscriptionIndex], ...updates };
            return { data: [mockSubscriptions[subscriptionIndex]], error: null };
          }
          return { data: null, error: new Error('Subscription not found in mock data') };
        }
        
        throw error;
      }
      
      return { data, error };
    } catch (err: any) {
      console.error('Error in subscription update:', err);
      return { data: null, error: err };
    }
  },

  // Delete subscription
  async delete(id: string) {
    try {
      if (!id) {
        throw new Error('Subscription ID is required for deletion');
      }
      
      const { data, error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting subscription from Supabase:', error);
        
        // Only use mock data in development
        if (import.meta.env.DEV) {
          console.warn('Using mock data as fallback in development');
          const subscriptionIndex = mockSubscriptions.findIndex(s => s.id === id);
          if (subscriptionIndex !== -1) {
            mockSubscriptions.splice(subscriptionIndex, 1);
            return { data: null, error: null };
          }
          return { data: null, error: new Error('Subscription not found') };
        }
      }
      
      return { data, error };
    } catch (err: any) {
      console.error('Error in subscription deletion:', err);
      return { data: null, error: err };
    }
  }
};

// Revenue tracking services
export const revenueServices = {
  // Get all revenue data
  async getAll() {
    const { data, error } = await supabase
      .from('revenue_data')
      .select('*')
      .order('date', { ascending: true });
    
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      return { data: mockRevenueData, error: null };
    }
    
    return { data, error };
  },

  // Get revenue data by date range
  async getByDateRange(startDate: string, endDate: string) {
    const { data, error } = await supabase
      .from('revenue_data')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const filteredData = mockRevenueData.filter(
        d => d.date >= startDate && d.date <= endDate
      );
      return { data: filteredData, error: null };
    }
    
    return { data, error };
  },

  // Create/update revenue data for a specific month
  async upsertMonthlyData(data: RevenueData) {
    // First try to get existing data for the date
    const { data: existingData } = await supabase
      .from('revenue_data')
      .select('id')
      .eq('date', data.date)
      .single();
    
    let result;
    
    if (existingData?.id) {
      // Update existing
      result = await supabase
        .from('revenue_data')
        .update(data)
        .eq('id', existingData.id)
        .select();
    } else {
      // Create new
      result = await supabase
        .from('revenue_data')
        .insert([data])
        .select();
    }
    
    if (result.error) {
      console.warn('Error upserting to Supabase, using mock data:', result.error);
      const existingIndex = mockRevenueData.findIndex(d => d.date === data.date);
      
      if (existingIndex !== -1) {
        // Update existing
        mockRevenueData[existingIndex] = { ...mockRevenueData[existingIndex], ...data };
        return { data: [mockRevenueData[existingIndex]], error: null };
      } else {
        // Create new
        const newData = { ...data, id: crypto.randomUUID() };
        mockRevenueData.push(newData);
        mockRevenueData.sort((a, b) => a.date.localeCompare(b.date));
        return { data: [newData], error: null };
      }
    }
    
    return result;
  }
};

// Tenant services
export const tenantServices = {
  // Get all tenants
  async getAll() {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      return { data: mockTenants, error: null };
    }
    
    return { data, error };
  },

  // Get tenant by id
  async getById(id: string) {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const mockTenant = mockTenants.find(t => t.id === id);
      return { data: mockTenant || null, error: mockTenant ? null : new Error('Tenant not found') };
    }
    
    return { data, error };
  },

  // Get tenants by company id
  async getByCompanyId(companyId: string) {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const filteredTenants = mockTenants.filter(t => t.company_id === companyId);
      return { data: filteredTenants, error: null };
    }
    
    return { data, error };
  },

  // Create a new tenant
  async create(tenant: Tenant) {
    // Ensure user_id is set if missing and user is authenticated
    if (!tenant.user_id) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        tenant.user_id = user.id;
      }
    }
    
    const { data, error } = await supabase
      .from('tenants')
      .insert([tenant])
      .select();
      
    if (error) {
      console.error('Error creating tenant in Supabase:', error.message, error.details, error.hint);
      console.warn('Error creating in Supabase, using mock data:', error);
      const newTenant = { ...tenant, id: crypto.randomUUID() };
      mockTenants.push(newTenant);
      return { data: [newTenant], error: null };
    }
    
    return { data, error };
  },

  // Update tenant
  async update(id: string, updates: Partial<Tenant>) {
    const { data, error } = await supabase
      .from('tenants')
      .update(updates)
      .eq('id', id)
      .select();
      
    if (error) {
      console.warn('Error updating in Supabase, using mock data:', error);
      const tenantIndex = mockTenants.findIndex(t => t.id === id);
      if (tenantIndex !== -1) {
        mockTenants[tenantIndex] = { ...mockTenants[tenantIndex], ...updates };
        return { data: [mockTenants[tenantIndex]], error: null };
      }
      return { data: null, error: new Error('Tenant not found') };
    }
    
    return { data, error };
  },

  // Delete tenant
  async delete(id: string) {
    const { data, error } = await supabase
      .from('tenants')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.warn('Error deleting from Supabase, using mock data:', error);
      const tenantIndex = mockTenants.findIndex(t => t.id === id);
      if (tenantIndex !== -1) {
        mockTenants.splice(tenantIndex, 1);
        return { data: null, error: null };
      }
      return { data: null, error: new Error('Tenant not found') };
    }
    
    return { data, error };
  }
};

// User services
export const userServices = {
  // Get all users
  async getAll() {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      return { data: mockUsers, error: null };
    }
    
    return { data, error };
  },

  // Get user by id
  async getById(id: string) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const mockUser = mockUsers.find(u => u.id === id);
      return { data: mockUser || null, error: mockUser ? null : new Error('User not found') };
    }
    
    return { data, error };
  },

  // Create a new user
  async create(user: User) {
    // Add detailed logging to troubleshoot connection issues
    try {
      const { data, error } = await supabase
        .from('users')
        .insert([user])
        .select();
        
      if (error) {
        console.error('Error creating user in Supabase:', error.message, error.details, error.hint);
        console.warn('Error creating in Supabase, using mock data:', error);
        const newUser = { ...user, id: crypto.randomUUID() };
        mockUsers.push(newUser);
        return { data: [newUser], error: null };
      }
      
      return { data, error };
    } catch (e) {
      console.error('Exception when creating user:', e);
      const newUser = { ...user, id: crypto.randomUUID() };
      mockUsers.push(newUser);
      return { data: [newUser], error: null };
    }
  },

  // Update user
  async update(id: string, updates: Partial<User>) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', id)
      .select();
      
    if (error) {
      console.warn('Error updating in Supabase, using mock data:', error);
      const userIndex = mockUsers.findIndex(u => u.id === id);
      if (userIndex !== -1) {
        mockUsers[userIndex] = { ...mockUsers[userIndex], ...updates };
        return { data: [mockUsers[userIndex]], error: null };
      }
      return { data: null, error: new Error('User not found') };
    }
    
    return { data, error };
  },

  // Delete user
  async delete(id: string) {
    const { data, error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.warn('Error deleting from Supabase, using mock data:', error);
      const userIndex = mockUsers.findIndex(u => u.id === id);
      if (userIndex !== -1) {
        mockUsers.splice(userIndex, 1);
        return { data: null, error: null };
      }
      return { data: null, error: new Error('User not found') };
    }
    
    return { data, error };
  }
};

// Invoice services
export const invoiceServices = {
  // Get all invoices
  async getAll() {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .order('due_date', { ascending: false });
    
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      return { data: mockInvoices, error: null };
    }
    
    return { data, error };
  },

  // Get invoice by id
  async getById(id: string) {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const mockInvoice = mockInvoices.find(i => i.id === id);
      return { data: mockInvoice || null, error: mockInvoice ? null : new Error('Invoice not found') };
    }
    
    return { data, error };
  },

  // Get invoices by subscription_id
  async getBySubscription(subscriptionId: string) {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('subscription_id', subscriptionId)
      .order('due_date', { ascending: false });
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const filteredInvoices = mockInvoices.filter(i => i.subscription_id === subscriptionId);
      return { data: filteredInvoices, error: null };
    }
    
    return { data, error };
  },

  // Create a new invoice
  async create(invoice: Invoice) {
    const { data, error } = await supabase
      .from('invoices')
      .insert([invoice])
      .select();
      
    if (error) {
      console.warn('Error creating in Supabase, using mock data:', error);
      const newInvoice = { ...invoice, id: crypto.randomUUID() };
      mockInvoices.push(newInvoice);
      return { data: [newInvoice], error: null };
    }
    
    return { data, error };
  },

  // Update invoice
  async update(id: string, updates: Partial<Invoice>) {
    const { data, error } = await supabase
      .from('invoices')
      .update(updates)
      .eq('id', id)
      .select();
      
    if (error) {
      console.warn('Error updating in Supabase, using mock data:', error);
      const invoiceIndex = mockInvoices.findIndex(i => i.id === id);
      if (invoiceIndex !== -1) {
        mockInvoices[invoiceIndex] = { ...mockInvoices[invoiceIndex], ...updates };
        return { data: [mockInvoices[invoiceIndex]], error: null };
      }
      return { data: null, error: new Error('Invoice not found') };
    }
    
    return { data, error };
  },

  // Delete invoice
  async delete(id: string) {
    const { data, error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.warn('Error deleting from Supabase, using mock data:', error);
      const invoiceIndex = mockInvoices.findIndex(i => i.id === id);
      if (invoiceIndex !== -1) {
        mockInvoices.splice(invoiceIndex, 1);
        return { data: null, error: null };
      }
      return { data: null, error: new Error('Invoice not found') };
    }
    
    return { data, error };
  }
};

// Payment services
export const paymentServices = {
  // Get all payments
  async getAll() {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .order('paid_at', { ascending: false });
    
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      return { data: mockPayments, error: null };
    }
    
    return { data, error };
  },

  // Get payment by id
  async getById(id: string) {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('id', id)
      .single();
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const mockPayment = mockPayments.find(p => p.id === id);
      return { data: mockPayment || null, error: mockPayment ? null : new Error('Payment not found') };
    }
    
    return { data, error };
  },

  // Get payments by invoice_id
  async getByInvoice(invoiceId: string) {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('paid_at', { ascending: false });
      
    if (error) {
      console.warn('Error fetching from Supabase, using mock data:', error);
      const filteredPayments = mockPayments.filter(p => p.invoice_id === invoiceId);
      return { data: filteredPayments, error: null };
    }
    
    return { data, error };
  },

  // Create a new payment
  async create(payment: Payment) {
    const { data, error } = await supabase
      .from('payments')
      .insert([payment])
      .select();
      
    if (error) {
      console.warn('Error creating in Supabase, using mock data:', error);
      const newPayment = { ...payment, id: crypto.randomUUID() };
      mockPayments.push(newPayment);
      return { data: [newPayment], error: null };
    }
    
    return { data, error };
  },

  // Update payment
  async update(id: string, updates: Partial<Payment>) {
    const { data, error } = await supabase
      .from('payments')
      .update(updates)
      .eq('id', id)
      .select();
      
    if (error) {
      console.warn('Error updating in Supabase, using mock data:', error);
      const paymentIndex = mockPayments.findIndex(p => p.id === id);
      if (paymentIndex !== -1) {
        mockPayments[paymentIndex] = { ...mockPayments[paymentIndex], ...updates };
        return { data: [mockPayments[paymentIndex]], error: null };
      }
      return { data: null, error: new Error('Payment not found') };
    }
    
    return { data, error };
  },

  // Delete payment
  async delete(id: string) {
    const { data, error } = await supabase
      .from('payments')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.warn('Error deleting from Supabase, using mock data:', error);
      const paymentIndex = mockPayments.findIndex(p => p.id === id);
      if (paymentIndex !== -1) {
        mockPayments.splice(paymentIndex, 1);
        return { data: null, error: null };
      }
      return { data: null, error: new Error('Payment not found') };
    }
    
    return { data, error };
  }
};
